import React from 'react';
import I18n from 'i18n-js';
import { get, defaults } from 'lodash';
import {
  core_apimessages_NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
  Listing,
} from '../rql';
import { UserInputOffer, Taxable } from './negotiation_types';
import Currency from '../currency';
import OfferQuantitySelect from './offer_quantity_select';
import { IUserContext, IUser, withUserContext } from '../components/user_context_provider';
import { isExperimentEnabled } from '../user_context_helpers';
import experiments from '../experiments';
import { RCTextInput } from '../rc/forms';

type OfferChangeHandler = (offer: Partial<UserInputOffer>) => void;
export interface IProps extends IUserContext {
  offer: UserInputOffer;
  listing: Listing;
  taxable: Taxable;
  currency: string;
  action: NegotiationAction;
  party: core_apimessages_NegotiationParty;
  onOfferChange: OfferChangeHandler;
  autoFocus?: boolean;
}

function taxHint(taxable: Taxable) {
  if (taxable && taxable.taxIncluded) {
    return ` (${taxable.taxIncludedHint})`;
  }

  return '';
}

function priceLabel(action: NegotiationAction, taxable: Taxable) {
  if (action === NegotiationAction.COUNTER) {
    return `${I18n.t('commons.offers.label.counterPrice')}${taxHint(taxable)}`;
  }

  return `${I18n.t('commons.offers.label.initialPrice')}${taxHint(taxable)}`;
}

function shippingPriceForBuyer(props: IProps) {
  if (props.party === core_apimessages_NegotiationParty.BUYER) {
    const shippingPrice = get(props, 'offer.shippingPrice', '');
    return I18n.t('commons.offers.shippingPrice', { shippingPrice });
  }
}

function moreThanOneInventoriedItem(listing: Listing) {
  return listing.hasInventory && listing.inventory > 1;
}

function userShouldSeeQuantitySelect(
  party: core_apimessages_NegotiationParty,
  user: IUser,
  lastOfferedQuantity: number) {
  return lastOfferedQuantity > 1 ||
    (party === core_apimessages_NegotiationParty.SELLER && isExperimentEnabled(user, experiments.OFFER_QUANTITY));
}

function displayQuantitySelect(props: Partial<IProps>) {
  const { action, listing, party, user, offer } = props;
  return (action === NegotiationAction.COUNTER || action === NegotiationAction.CREATE) &&
    moreThanOneInventoriedItem(listing) &&
    userShouldSeeQuantitySelect(party, user, offer.quantity);
}

function OfferAmountInputs(props: IProps) {
  const { symbol, displayCode } = defaults(Currency.get(props.currency), { symbol: '', displayCode: '' });
  const shippingPrice = shippingPriceForBuyer(props);

  return (
    <div className="offers-form__price-row">
      <div className="offers-form__price">
        <RCTextInput
          label={priceLabel(props.action, props.taxable)}
          prefixText={symbol}
          suffixText={[displayCode, shippingPrice].join(' ').trim()}
          name="offer-price"
          id="offer-price"
          value={props.offer.price}
          onChange={(evt) => props.onOfferChange({ price: evt.target.value })}
          type="number"
          required
        />
      </div>
      {displayQuantitySelect(props) &&
        <div className="offers-form__qty">
          <OfferQuantitySelect
            inventory={props.listing.inventory}
            party={props.party}
            quantity={props.offer.quantity}
            onQuantityChange={props.onOfferChange}
          />
        </div>
      }
    </div>
  );
}

export default withUserContext(OfferAmountInputs);
